// ────────────────────────────────────────────────────────────────────────────
// Straight-line geometry and the road-time FALLBACK, in one place.
//
// There were four copies of this: `orders/lib/journeyPlan`, two identical
// `driveMinFor` constants in `bookings/lib/bookingDetail` and
// `bookings/lib/recommendRoute`, and a private `haversineKm` in
// `manifest/lib/suggestLoads`. Four copies of a heuristic is four chances for
// two screens to quote different drive times for the same road, and nothing
// would have flagged it.
//
// WHEN TO USE WHICH:
//
//   • `haversineKm` / `distanceKm` — for CHOOSING. Which drops are near each
//     other, which port is closest, what to cluster. Free, instant, runs over
//     O(n²) pairs without thinking about it, and relative closeness is all
//     these answers need.
//
//   • `roadMinutesFor` — a FALLBACK ONLY, for a leg no routing service could
//     answer. It is a car's average speed on a car's roads and it reads
//     plausible, which is what makes it dangerous: Waterford to Limerick comes
//     out at 1h43 against a real truck run of over two hours. Anything shown to
//     a person off the back of this must be marked as an estimate.
//
// Real driving times come from Azure Maps in truck mode, server-side — see
// Directory's route cache — `POST /directory/api/v1/maps/road-legs` and `useRoadLegs`.
// ────────────────────────────────────────────────────────────────────────────

const EARTH_KM = 6371;

/** Great-circle km between two coordinate pairs. */
export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** The same, over nullable coordinate holders — null when either is unknown. */
export function distanceKm(
  a: { latitude?: number | null; longitude?: number | null },
  b: { latitude?: number | null; longitude?: number | null },
): number | null {
  if (a.latitude == null || a.longitude == null || b.latitude == null || b.longitude == null) {
    return null;
  }
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
}

/**
 * Road km from a straight line, inflated because roads bend and ferries do not.
 * 1.25 is the usual planning fudge for north-west Europe.
 */
export const ROAD_WINDING_FACTOR = 1.25;

export function estimatedRoadKm(
  a: { latitude?: number | null; longitude?: number | null },
  b: { latitude?: number | null; longitude?: number | null },
): number | null {
  const straight = distanceKm(a, b);
  return straight == null ? null : Math.round(straight * ROAD_WINDING_FACTOR);
}

/**
 * Fallback drive minutes for a distance: roughly 79 km/h door to door, floored
 * at a quarter of an hour because no leg is instant.
 *
 * <p>
 * Deliberately NOT tuned to be more truck-like. Making it 65 km/h would produce
 * numbers that look defensible and still be guesses, which is worse than an
 * obvious estimate — the fix for a wrong drive time is routing the leg, not a
 * better constant.
 * </p>
 */
export function roadMinutesFor(km: number): number {
  return Math.max(15, Math.round(km * 0.76));
}
